import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import Sidebar from './components/layout/Sidebar';
import StatusBar from './components/layout/StatusBar';
import BottomTabBar from './components/BottomTabBar';
import FloatingActionButton from './components/FloatingActionButton';
import { SupportWidget } from './components/support/components/SupportWidget';
import { kogSupportConfig } from './supportConfig';
import DashboardPage from './pages/DashboardPage';
import ContactsPage from './pages/ContactsPage';
import OrganizationsPage from './pages/OrganizationsPage';
import PipelinePage from './pages/PipelinePage';
import ActivitiesPage from './pages/ActivitiesPage';
import AnalyticsPage from './pages/AnalyticsPage';
import DocumentsPage from './pages/DocumentsPage';
import VoicePage from './pages/VoicePage';
import SettingsPage from './pages/SettingsPage';
import ProductsPage from './pages/ProductsPage';
import BillingPage from './pages/BillingPage';
import CampaignsPage from './pages/CampaignsPage';
import CampaignDetailPage from './pages/CampaignDetailPage';
import JobSearchPage from './pages/JobSearchPage';
import ContactDetailPage from './pages/ContactDetailPage';
import ClientPrepPage from './pages/ClientPrepPage';
import FreelancerDashboard from './pages/FreelancerDashboard';
import LinkedInImportPage from './pages/LinkedInImportPage';
import NetworkInsightsPage from './pages/NetworkInsightsPage';
import RemindersPage from './pages/RemindersPage';
import CampaignAttributionPage from './pages/CampaignAttributionPage';
import EnrichmentPage from './pages/EnrichmentPage';
import RevenueDashboard from './pages/RevenueDashboard';
import SmartListsPage from './pages/SmartListsPage';
import IntelligencePage from './pages/IntelligencePage';
import DedupPage from './pages/DedupPage';
import LoginPage from './pages/LoginPage';
import OAuthCallbackPage from './pages/OAuthCallbackPage';
import RegisterPage from './pages/RegisterPage';
import OnboardingPage from './pages/OnboardingPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import CookieConsent from './components/CookieConsent';
import GoogleAnalytics from './components/GoogleAnalytics';

function isAuthenticated(): boolean {
  return !!localStorage.getItem('kog_access_token');
}

function ProtectedRoute() {
  const location = useLocation();

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return (
    <div className="flex min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      {/* Sidebar: hidden on mobile, visible on md+ — includes spacer div that pushes content */}
      <div className="hidden md:flex">
        <Sidebar />
      </div>
      <main className="flex-1 min-w-0 pb-20 md:pb-10">
        <Outlet />
      </main>
      {/* StatusBar: hidden on mobile */}
      <div className="hidden md:block">
        <StatusBar />
      </div>
      {/* BottomTabBar: visible only on mobile */}
      <BottomTabBar />
      {/* FAB: visible on all screens */}
      <FloatingActionButton />
      {/* Support Widget — contextual help, tutorials, docs, Charlotte AI */}
      <SupportWidget
        config={{
          ...kogSupportConfig,
          className: `kog-support-widget route-${location.pathname.replace(/\//g, '-') || 'home'}`,
        }}
      />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <GoogleAnalytics />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/callback" element={<OAuthCallbackPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />

        <Route element={<ProtectedRoute />}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/contacts/:id" element={<ContactDetailPage />} />
          <Route path="/clients/:id/prep" element={<ClientPrepPage />} />
          <Route path="/freelancer" element={<FreelancerDashboard />} />
          <Route path="/organizations" element={<OrganizationsPage />} />
          <Route path="/pipeline" element={<PipelinePage />} />
          <Route path="/activities" element={<ActivitiesPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/campaigns" element={<CampaignsPage />} />
          <Route path="/campaigns/:id" element={<CampaignDetailPage />} />
          <Route path="/job-search" element={<JobSearchPage />} />
          <Route path="/linkedin-import" element={<LinkedInImportPage />} />
          <Route path="/network-insights" element={<NetworkInsightsPage />} />
          <Route path="/reminders" element={<RemindersPage />} />
          <Route path="/attribution" element={<CampaignAttributionPage />} />
          <Route path="/enrichment" element={<EnrichmentPage />} />
          <Route path="/revenue" element={<RevenueDashboard />} />
          <Route path="/smart-lists" element={<SmartListsPage />} />
          <Route path="/intelligence" element={<IntelligencePage />} />
          <Route path="/dedup" element={<DedupPage />} />
          <Route path="/voice" element={<VoicePage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <CookieConsent />
    </BrowserRouter>
  );
}
